import { loadStripe, Stripe } from '@stripe/stripe-js';
import * as Sentry from '@sentry/nextjs';
import { createContext, useContext, useEffect, useState } from 'react';
import { isLikelyBot } from '@util/bot-detection';

type StripeContextType = {
  stripePromise: PromiseLike<Stripe | null> | Stripe | null;
  testStripePromise: PromiseLike<Stripe | null> | Stripe | null;
  isLoading: boolean;
  error: string | null;
};

const StripeContext = createContext<StripeContextType>({
  stripePromise: null,
  testStripePromise: null,
  isLoading: true,
  error: null,
});

export function useStripeContext() {
  return useContext(StripeContext);
}

export function StripeProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<StripeContextType>({
    stripePromise: null,
    testStripePromise: null,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    let isMounted = true;

    const initializeStripe = async () => {
      try {
        // Skip Stripe entirely for bots (Googlebot, Bingbot, headless
        // crawlers, etc.). Stripe.js refuses to initialize for known bots
        // anyway, and that rejection used to surface as a checkout-failure
        // 911 alert + Sentry noise whenever a crawler hit /checkout. Bots
        // never transact, so there's nothing to load — bail cleanly with
        // no error state. Real users are unaffected. (Runs inside useEffect,
        // so `navigator` is always defined here.)
        if (isLikelyBot(navigator.userAgent)) {
          if (isMounted) {
            setState({
              stripePromise: null,
              testStripePromise: null,
              isLoading: false,
              error: null,
            });
          }
          return;
        }

        // Add timeout to detect blocked Stripe
        const STRIPE_TIMEOUT = 8000; // 8 seconds - balances slow connections vs acceptable wait time

        const loadWithTimeout = async (
          key: string
        ): Promise<Stripe | null> => {
          return Promise.race([
            loadStripe(key),
            new Promise<null>((_, reject) =>
              setTimeout(
                () => reject(new Error('Stripe load timeout - possibly blocked')),
                STRIPE_TIMEOUT
              )
            ),
          ]);
        };

        // Load both Stripe instances with timeout
        const [stripe, testStripe] = await Promise.all([
          loadWithTimeout(process.env.NEXT_PUBLIC_STRIPE_PK!),
          loadWithTimeout(process.env.NEXT_PUBLIC_TEST_STRIPE_PK!).catch(() => null), // Test stripe is optional
        ]);

        if (isMounted) {
          // Check if Stripe loaded successfully
          if (!stripe) {
            throw new Error('Stripe failed to initialize - returned null');
          }

          setState({
            stripePromise: stripe,
            testStripePromise: testStripe,
            isLoading: false,
            error: null,
          });
        }
      } catch (error) {
        console.error('[StripeContext] Failed to load Stripe:', error);

        // Only log to Sentry if user is on checkout page (where they actually need Stripe)
        // Stripe may be blocked by ad blockers, but we don't want noise from non-checkout pages
        const isCheckoutPage = typeof window !== 'undefined' && window.location.pathname === '/checkout';

        const errorMessage = error instanceof Error ? error.message : '';
        const isTimeout = errorMessage.includes('timeout');
        const stripeFailureType = isTimeout ? 'timeout' : 'blocked';

        if (isCheckoutPage) {
          Sentry.captureException(error, {
            tags: {
              location: 'stripe_context',
              action: 'stripe_initialization',
              stripe_failure_type: stripeFailureType,
            },
            extra: {
              userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : 'unknown',
            },
          });
        }

        if (isMounted) {
          setState({
            stripePromise: null,
            testStripePromise: null,
            isLoading: false,
            error: isTimeout
              ? 'Something took longer than expected. Please try again — your payment info is safe and you won\'t be charged twice.'
              : 'Your payment didn\'t go through. If you\'re using an ad blocker, try pausing it for this page — some blockers prevent secure payment connections. You can also try a different browser.',
          });
        }
      }
    };

    initializeStripe();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <StripeContext.Provider value={state}>
      {children}
    </StripeContext.Provider>
  );
}
